import { useCallback } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import type { NormalizedFormValues } from '#src/app/pages/petitionDetails/shared/types';
import { identifyConstituentByScope, useConstituentStatus } from '#src/app/shared/constituents';

type Result = (formData: NormalizedFormValues) => void;

/**
 * Stores the constituent-identification result at sign time, where the signer's submitted address is
 * available, for the combo screen to read on its next page load.
 *
 * Always writes (null for ineligible signs) so a later combo-screen visit in the same tab cannot read
 * a stale per-petition result from an earlier sign. Owns its own try/catch so identification can never
 * turn an already-recorded signature into a submit error.
 */
export function useSetSessionStoredConstituentStatus(): Result {
	const { errorReporter } = useUtilityContext();
	const {
		data: { petition },
	} = usePetitionDetailsPageData();
	const [, setConstituentStatus] = useConstituentStatus(petition.id);

	return useCallback(
		(formData) => {
			try {
				setConstituentStatus(
					petition.constituentExperimentEligible
						? identifyConstituentByScope(
								{
									countryCode: petition.country.countryCode,
									stateCode: petition.state?.stateCode,
									city: petition.city,
								},
								{ countryCode: formData.countryCode, stateCode: formData.stateCode, city: formData.city },
							)
						: null,
				);
			} catch (error) {
				void errorReporter.report({
					error: error instanceof Error ? error : new Error('Constituent identification failed', { cause: error }),
					context: { feature: 'sign_form' },
					params: { petitionId: petition.id },
				});
			}
		},
		[petition, setConstituentStatus, errorReporter],
	);
}
